import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, curSym, fmtDate, errMsg, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportToCsv } from "@finance/lib/exportCsv";
import { StatusBadge } from "@finance/components/StatusBadge";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Switch } from "@finance/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@finance/components/ui/sheet";
import { ComplianceTab } from "@finance/components/ComplianceTab";
import { toast } from "sonner";
import { Plus, DownloadSimple, Trash, Truck, Buildings } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";
import { BulkUploadButton } from "@finance/components/BulkUpload";
import { ApTieout } from "@finance/components/CardsAp";

const FIELDS = [
  ["name", "Name"], ["tin", "TIN"], ["vrn", "VRN"], ["contact_person", "Contact Person"],
  ["phone", "Phone"], ["email", "Email(s) — separate multiple with commas; all receive statements"], ["payment_terms", "Payment Terms"], ["default_brand", "Default Brand"],
];

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ is_import_supplier: false });
  const [detail, setDetail] = useState(null);
  const [detailTxns, setDetailTxns] = useState([]);
  const [sel, setSel] = useState([]);

  const load = useCallback(() => api.get("/suppliers").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      if (form.id) await api.put(`/suppliers/${form.id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Supplier saved");
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = async (s) => {
    setDetail(s);
    const r = await api.get(`/suppliers/${s.id}/transactions`);
    setDetailTxns(r.data);
  };

  const del = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/suppliers/${s.id}`); toast.success("Supplier deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const delSelected = async () => {
    if (!window.confirm(`Delete ${sel.length} supplier(s)?`)) return;
    let fail = 0;
    for (const id of sel) { try { await api.delete(`/suppliers/${id}`); } catch { fail += 1; } }
    toast.success(`Deleted ${sel.length - fail}${fail ? `, ${fail} blocked (in use)` : ""}`); setSel([]); load();
  };

  const cols = [...FIELDS.map(([key, label]) => ({ key, label })), { key: "is_import_supplier", label: "Import Supplier", format: (v) => (v ? "Yes" : "No") }];

  return (
    <Layout>
      <PageHeader
        title="Suppliers"
        subtitle={`${rows.length} suppliers`}
        actions={
          <>
            {sel.length > 0 && <Button variant="outline" className="border-red-200 text-red-600" data-testid="delete-selected-suppliers" onClick={delSelected}><Trash size={16} className="mr-1" />Delete ({sel.length})</Button>}
            <Button variant="outline" data-testid="export-suppliers" onClick={() => exportToCsv("suppliers", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
            <BulkUploadButton entity="suppliers" onDone={load} />
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-supplier-btn" onClick={() => { setForm({ is_import_supplier: false }); setOpen(true); }}><Plus size={16} className="mr-1" />New Supplier</Button>
          </>
        }
      />
      <div className="p-8">
        <KpiStrip cols={3}>
          <KpiCard icon={Buildings} label="Total Suppliers" value={rows.length} testid="kpi-suppliers-count" />
          <KpiCard icon={Truck} label="Import Suppliers" value={rows.filter((s) => s.is_import_supplier).length} tone="info" testid="kpi-suppliers-import" />
          <KpiCard icon={Buildings} label="With TIN" value={rows.filter((s) => s.tin).length} tone="success" testid="kpi-suppliers-tin" />
        </KpiStrip>
        {rows.length === 0 ? (
          <EmptyState icon={Buildings} testid="suppliers-empty" title="No suppliers yet" message="Add your first supplier or bulk-upload a list to get started." />
        ) : (
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-3 py-2"><input type="checkbox" data-testid="select-all-suppliers" checked={rows.length > 0 && sel.length === rows.length} onChange={() => setSel(sel.length === rows.length ? [] : rows.map((r) => r.id))} /></th>
              <th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">TIN</th>
              <th className="px-4 py-2 text-left">Terms</th><th className="px-4 py-2 text-left">Import</th>
              <th className="px-4 py-2 text-left">Phone</th><th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="cursor-pointer border-b border-gray-100 hover:bg-gray-50" onClick={() => openDetail(s)}>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`select-supplier-${s.id}`} checked={sel.includes(s.id)} onChange={() => toggle(s.id)} /></td>
                  <td className="px-4 py-2 font-medium">{s.name}</td>
                  <td className="px-4 py-2 font-mono-num">{s.tin || "—"}</td>
                  <td className="px-4 py-2">{s.payment_terms || "—"}</td>
                  <td className="px-4 py-2">{s.is_import_supplier ? <span className="rounded-sm bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">Import</span> : "—"}</td>
                  <td className="px-4 py-2 font-mono-num">{s.phone || "—"}</td>
                  <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <button className="text-xs text-gray-500 hover:text-[#1B4383]" data-testid={`edit-supplier-${s.id}`} onClick={() => { setForm(s); setOpen(true); }}>Edit</button>
                    <button className="ml-3 text-xs text-gray-400 hover:text-red-500" data-testid={`delete-supplier-${s.id}`} onClick={() => del(s)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit Supplier" : "New Supplier"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label className="lx-label">{label}</Label>
                <Input data-testid={`supplier-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1" />
              </div>
            ))}
            <div className="col-span-2 flex items-center gap-3">
              <Switch checked={!!form.is_import_supplier} data-testid="supplier-import-toggle" onCheckedChange={(v) => setForm({ ...form, is_import_supplier: v })} />
              <Label className="lx-label">Import Supplier</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-supplier" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto">
          <SheetHeader><SheetTitle className="font-display">{detail?.name}</SheetTitle></SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4">
              <Button size="sm" variant="outline" className="w-full" data-testid="supplier-statement-pdf" onClick={() => openPdf(`pdf/supplier-statement/${detail.id}`)}>Download Statement (PDF)</Button>
              <ApTieout sid={detail.id} />
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="lx-label">TIN</div><div className="font-mono-num">{detail.tin || "—"}</div></div>
                <div><div className="lx-label">Terms</div><div>{detail.payment_terms || "—"}</div></div>
              </div>
              <div>
                <div className="lx-label mb-2">Transaction History ({detailTxns.length})</div>
                <div className="space-y-2">
                  {detailTxns.length === 0 && <div className="text-sm text-gray-400">No transactions.</div>}
                  {detailTxns.map((t) => (
                    <div key={t.id} className="rounded-sm border border-gray-200 p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-mono-num font-medium">{t.lx_po_no || "—"}</span>
                        <StatusBadge status={t.status} />
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{curSym(t.currency)} {fmtNum(t.payable_amount)} · {fmtDate(t.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {detail && <div className="mt-4"><ComplianceTab ptype="supplier" pid={detail.id} /></div>}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
